import { MemberRemovalReason } from '@/app-shell/settings/organization/atoms/organizationAtoms';
import { PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { UserRole } from '@heidi/schema/ml-scribe';
import { type OrganizationMembersView } from '@heidi/workspace/contracts';
import {
  setSelectedOrganizationId,
  useQueryJoinedOrganizations,
  useSelectedOrganizationId,
} from '@heidi/workspace/data';
import { useCallback, useEffect } from 'react';
import { useQueryOrganizationById } from './useQueryOrganization';
import { useQueryRoles } from './useQueryRoles';

export const useCombinedOrganizationInfo = () => {
  const {
    data: joinedOrganizationData,
    isFetched: isJoinedOrganizationFetched,
    isPending: isLoadingJoinedOrganization,
  } = useQueryJoinedOrganizations();
  const selectedOrganizationId = useSelectedOrganizationId();

  useEffect(() => {
    if (!joinedOrganizationData) return;
    const newOrgId = joinedOrganizationData.organizations.at(0)?.org_id ?? null;
    if (newOrgId !== selectedOrganizationId) {
      setSelectedOrganizationId(newOrgId);
    }
  }, [joinedOrganizationData, selectedOrganizationId]);

  const firstJoinedOrg = joinedOrganizationData?.organizations?.at(0);
  const isGlobalAdmin =
    firstJoinedOrg?.is_in_org_team && firstJoinedOrg?.user_role === UserRole.ADMIN;

  const isRootTeamTemplateManager = Boolean(
    firstJoinedOrg?.is_in_org_team &&
    firstJoinedOrg?.user_role_list?.some((role) =>
      role.permissions?.some(
        (permission) => permission.code === PERMISSION_SUBJECTS.TEMPLATE_SHARING_MANAGE
      )
    )
  );

  const isOrganizationTemplateManager = Boolean(
    isRootTeamTemplateManager || isGlobalAdmin
  );

  const { data: organizationDetails, isPending: isLoadingOrganizationDetails } =
    useQueryOrganizationById(
      selectedOrganizationId ?? undefined,
      isOrganizationTemplateManager
    );

  const isLoadingOrganization =
    isLoadingJoinedOrganization || isLoadingOrganizationDetails;
  const { data: orgRolesData } = useQueryRoles(organizationDetails?.organization.id);
  const orgRoles = orgRolesData?.data;
  const orgTeams = organizationDetails?.organization.teams;
  // Users in top level org team are global admins
  const canMemberBeRemoved = useCallback(
    (member: OrganizationMembersView) => {
      if (member.team_id === selectedOrganizationId) {
        return { canRemove: false, reason: MemberRemovalReason.GLOBAL_ADMIN };
      }
      if (member.is_team_owner) {
        return { canRemove: false, reason: MemberRemovalReason.TEAM_OWNER };
      }
      return { canRemove: true, reason: null };
    },
    [selectedOrganizationId]
  );

  const canMembersTeamBeChanged = useCallback(
    // Global admins, and team owners cannot be changed
    (member: OrganizationMembersView) => {
      if (member.team_id === selectedOrganizationId) {
        return { canRemove: false, reason: MemberRemovalReason.GLOBAL_ADMIN };
      }
      if (member.is_team_owner) {
        return { canRemove: false, reason: MemberRemovalReason.TEAM_OWNER };
      }
      return { canRemove: true, reason: null };
    },
    [selectedOrganizationId]
  );

  const hasOrganization = isJoinedOrganizationFetched && !!firstJoinedOrg?.org_id;
  const allowSessionLinkSharing = hasOrganization
    ? (firstJoinedOrg?.allow_session_link_sharing ?? false)
    : isJoinedOrganizationFetched;

  return {
    isGlobalAdmin,
    joinedOrganizationData,
    isJoinedOrganizationFetched,
    organizationDetails,
    isLoadingOrganizationDetails,
    isLoadingJoinedOrganization,
    isLoadingOrganization,
    orgRoles,
    orgTeams,
    canMemberBeRemoved,
    canMembersTeamBeChanged,
    allowSessionLinkSharing,
    hasOrganization,
    isOrganizationTemplateManager,
  };
};
